import React, { useState, useEffect, useRef, useCallback } from 'react';
import { apiGet, apiPost } from './apiUtils';
import { getCurrentUser } from './authUtils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './components/ui/card';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Badge } from './components/ui/badge';
import { Label } from './components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './components/ui/table';
import { Alert, AlertDescription } from './components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { 
  Search,
  ArrowDownCircle, 
  ArrowUpCircle, 
  Trash2, 
  CheckCircle2,
  AlertCircle,
  ArrowDownToLine,
  ArrowUpFromLine
} from 'lucide-react';

function ShiftTransfer() {
  console.log('🔍 ShiftTransfer - Component rendering');
  // Match IndentEstimate search behavior
  const SEARCH_DEBOUNCE_DELAY = 150;
  const MIN_SEARCH_LENGTH = 2;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Form state
  const [shiftType, setShiftType] = useState('in'); // 'in' or 'out'
  const [selectedStore, setSelectedStore] = useState('');
  const [stores, setStores] = useState([]);
  const [tenTimesCompleted, setTenTimesCompleted] = useState(false); // For TGBCL retail excise tax
  
  // Product search state
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [shopProductsCache, setShopProductsCache] = useState([]);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [rowErrors, setRowErrors] = useState({});
  
  // Refs for search functionality (mirror IndentEstimate)
  const searchTimeoutRef = useRef(null);
  const searchInputRef = useRef(null);
  const searchContainerRef = useRef(null);
  const abortControllerRef = useRef(null);
  
  const user = getCurrentUser();
  const shopName = user?.shopName || 'Unknown Shop';
  // Clear store selection when switching shift types if incompatible
  useEffect(() => {
    if (shiftType === 'out' && selectedStore === 'tgbcl') {
      // Clear TGBCL when switching to Shift Out
      setSelectedStore('');
    } else if (shiftType === 'in' && selectedStore && selectedStore.startsWith('store_')) {
      // Clear internal stores when switching to Shift In
      setSelectedStore('');
    }
  }, [shiftType, selectedStore]);
  const formatINR = (amount) => {
    const num = parseFloat(amount || 0);
    return `₹${num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  // Prefetch shop inventory brands when shifting out
  useEffect(() => {
    console.log('🔍 ShiftTransfer - loadShopProducts useEffect triggered', { shiftType, shopProductsCacheLength: shopProductsCache.length });
    const loadShopProducts = async () => {
      try {
        console.log('🔍 ShiftTransfer - loadShopProducts called');
        const response = await apiGet('/api/shop/products');
        const data = await response.json();
        const items = Array.isArray(data) ? data : (data.products || data || []);
        console.log('🔍 ShiftTransfer - loadShopProducts items loaded:', items.length);
        setShopProductsCache(items);
      } catch (e) {
        console.error('Failed to load shop products:', e);
      }
    };
    if (shiftType === 'out' && shopProductsCache.length === 0) {
      loadShopProducts();
    }
  }, [shiftType, shopProductsCache.length]);


  // Auto-dismiss success message
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess('');
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Fetch stores when shift type changes
  useEffect(() => {
    const fetchStores = async () => {
      try {
        console.log('🔍 ShiftTransfer - fetchStores called, shiftType:', shiftType);
        
        let allStores = [];

        // Get current shop data from session (secure) - called inside effect to avoid dependency
        const currentShop = getCurrentUser();
        console.log('🔍 ShiftTransfer - Current shop from session:', currentShop);

        // Fetch stores from the unified stores endpoint with operation type filter
        try {
          const operationParam = shiftType === 'in' ? 'shift-in' : 'shift-out';
          const storesResponse = await apiGet(`/api/stores?operation=${operationParam}`);
          const storesData = await storesResponse.json();
          console.log('🔍 ShiftTransfer - Stores data for', operationParam, ':', storesData);
          
          // Map stores data to expected format
          const storesAsStores = storesData.map(store => ({
            id: store.id === 'tgbcl' ? 'tgbcl' : `store_${store.id}`,
            shop_name: store.shop_name,
            retailer_code: store.retailer_code,
            contact: store.contact,
            source: store.store_type || 'store'
          }));
          
          allStores.push(...storesAsStores);
        } catch (storesError) {
          console.error('🔍 ShiftTransfer - Error fetching stores:', storesError);
        }

        console.log('🔍 ShiftTransfer - Final stores array for', shiftType, ':', allStores);
        setStores(allStores);
      } catch (error) {
        console.error('Error fetching stores:', error);
        setError('Failed to load stores');
      }
    };

    fetchStores();
  }, [shiftType]); // Only depend on shiftType

  // Product search identical to IndentEstimate (debounced + abortable)
  const handleSearch = useCallback(async (term) => {
    const searchQuery = (term || searchTerm).trim();
    if (!searchQuery || searchQuery.length < MIN_SEARCH_LENGTH) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }
    if (shiftType === 'out') {
      // Shift Out: Search current shop inventory via API
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      try {
        const response = await apiGet(`/api/shop/products?search=${encodeURIComponent(searchQuery)}`);
        const data = await response.json();
        const results = Array.isArray(data) ? data : (data.products || []);
        const normalized = results.map(r => ({
          id: r.id,
          name: (r.brand_name || r.name || '').trim(),
          brandNumber: r.brand_number || r.brandNumber,
          packQuantity: Number(r.pack_quantity || r.packQuantity || 12),
          sizeMl: Number(r.size_ml || r.size || 0),
          mrp: r.standard_mrp ?? r.mrp ?? 0,
          packType: r.pack_type || r.packType || '',
          available: Number(
            // Prefer daily closing for the business date, else total stock, else raw quantity
            (r.closingStock ?? r.closing_stock) ??
            (r.totalStock ?? r.total_stock) ??
            (r.quantity ?? r.current_quantity) ?? 0
          )
        }));
        setSearchResults(normalized);
        setShowSearchResults(true);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Search error:', error);
          setSearchResults([]);
          setShowSearchResults(false);
        }
      }
      return;
    }

    // Shift In: Search based on store type
    if (!selectedStore) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }
    
    // Check store type first
    if (selectedStore === 'tgbcl') {
      // TGBCL: Always external, search all master brands
      console.log('🔍 Searching master brands for TGBCL');
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      try {
        const response = await apiGet(`/api/search-brands?q=${encodeURIComponent(searchQuery)}`);
        const data = await response.json();
        const results = Array.isArray(data) ? data : (data.brands || []);
        const normalized = results.map(r => ({
          id: r.id,
          name: (r.brand_name || r.name || '').trim(),
          brandNumber: r.brand_number || r.brandNumber,
          packQuantity: Number(r.pack_quantity || r.packQuantity || 12),
          sizeMl: Number(r.size_ml || r.size || 0),
          mrp: r.standard_mrp ?? r.mrp ?? 0,
          packType: r.pack_type || r.packType || ''
        }));
        setSearchResults(normalized);
        setShowSearchResults(true);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error searching brands:', error);
          setSearchResults([]);
          setShowSearchResults(false);
        }
      }
    } else if (selectedStore?.startsWith('store_')) {
      // Check if store is internal or external
      const storeId = selectedStore.replace('store_', '');
      console.log('🔍 Checking store type for store ID:', storeId);
      
      try {
        const typeResponse = await apiGet(`/api/check-supplier-type?supplierId=${storeId}`);
        const typeData = await typeResponse.json();
        console.log('🔍 Supplier type result:', typeData);
          
          if (typeData.isInternal) {
            // Internal supplier: Search that shop's inventory
            console.log('🔍 Searching internal shop inventory for shop ID:', typeData.shopInfo.id);
            if (abortControllerRef.current) {
              abortControllerRef.current.abort();
            }
            abortControllerRef.current = new AbortController();

            const response = await apiGet(`/api/shop/products?shopId=${typeData.shopInfo.id}&search=${encodeURIComponent(searchQuery)}`);
            const data = await response.json();
            const results = Array.isArray(data) ? data : (data.products || []);
            const normalized = results.map(r => ({
              id: r.id,
              name: (r.brand_name || r.name || '').trim(),
              brandNumber: r.brand_number || r.brandNumber,
              packQuantity: Number(r.pack_quantity || r.packQuantity || 12),
              sizeMl: Number(r.size_ml || r.size || 0),
              mrp: r.standard_mrp ?? r.mrp ?? 0,
              packType: r.pack_type || r.packType || '',
              available: Number(r.current_quantity || r.quantity || 0)
            }));
            setSearchResults(normalized);
            setShowSearchResults(true);
          } else {
            // External supplier: Search all master brands
            console.log('🔍 Searching master brands for external supplier:', typeData.supplierName);
            if (abortControllerRef.current) {
              abortControllerRef.current.abort();
            }
            abortControllerRef.current = new AbortController();

            const response = await apiGet(`/api/search-brands?q=${encodeURIComponent(searchQuery)}`);
            const data = await response.json();
            const results = Array.isArray(data) ? data : (data.brands || []);
            const normalized = results.map(r => ({
              id: r.id,
              name: (r.brand_name || r.name || '').trim(),
              brandNumber: r.brand_number || r.brandNumber,
              packQuantity: Number(r.pack_quantity || r.packQuantity || 12),
              sizeMl: Number(r.size_ml || r.size || 0),
              mrp: r.standard_mrp ?? r.mrp ?? 0,
              packType: r.pack_type || r.packType || ''
            }));
            setSearchResults(normalized);
            setShowSearchResults(true);
          }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error checking supplier type:', error);
          setSearchResults([]);
          setShowSearchResults(false);
        }
      }
    } else {
      // Fallback: External store, search all master brands
      console.log('🔍 Searching master brands for external store (fallback):', selectedStore);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      try {
        const response = await apiGet(`/api/search-brands?q=${encodeURIComponent(searchQuery)}`);
        const data = await response.json();
        const results = Array.isArray(data) ? data : (data.brands || []);
        const normalized = results.map(r => ({
          id: r.id,
          name: (r.brand_name || r.name || '').trim(),
          brandNumber: r.brand_number || r.brandNumber,
          packQuantity: Number(r.pack_quantity || r.packQuantity || 12),
          sizeMl: Number(r.size_ml || r.size || 0),
          mrp: r.standard_mrp ?? r.mrp ?? 0,
          packType: r.pack_type || r.packType || ''
        }));
        setSearchResults(normalized);
        setShowSearchResults(true);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error searching brands:', error);
          setSearchResults([]);
          setShowSearchResults(false);
        }
      }
    }
  }, [searchTerm, shiftType, selectedStore]);

  const handleInputChange = useCallback((e) => {
    const value = e.target.value.slice(0, 100);
    setSearchTerm(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      handleSearch(value);
    }, SEARCH_DEBOUNCE_DELAY);
  }, [handleSearch]);

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      handleSearch();
    } else if (e.key === 'Escape') {
      setShowSearchResults(false);
      setSearchTerm('');
    }
  }, [handleSearch]);

  // Close results on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleBrandSelect = (brand) => {
    console.log('🔍 Frontend - Brand selected from search:', brand);
    
    // Normalize fields; include pack quantity and size ml
    const normalized = {
      id: brand.id,
      name: (brand.brand_name || brand.name || '').trim(),
      brandNumber: brand.brand_number || brand.brandNumber,
      packQuantity: Number(brand.pack_quantity || brand.packQuantity || 12),
      sizeMl: Number(brand.sizeMl ?? brand.size_ml ?? brand.size ?? 0),
      mrp: Number(brand.standard_mrp ?? brand.mrp ?? 0),
      packType: brand.packType || brand.pack_type || '',
      available: Number(brand.available || 0) // Include available quantity
    };
    
    console.log('🔍 Frontend - Normalized product:', normalized);

    const isAlreadySelected = selectedProducts.some(p => p.id === normalized.id);
    if (isAlreadySelected) {
      setError('This product is already added');
      setTimeout(() => setError(''), 3000);
      return;
    }

    // Add normalized brand to selected products with cases/bottles
    const newProduct = { ...normalized, cases: 0, bottles: 0 };

    setSelectedProducts(prev => [...prev, newProduct]);
    setSearchTerm('');
    setSearchResults([]);
    setShowSearchResults(false);
    setError('');
  };

  const handleCasesChange = (productId, value) => {
    const num = Math.max(0, parseInt(value) || 0);
    console.log('🔍 Frontend - handleCasesChange:', { productId, value, num });
    setSelectedProducts(prev => {
      const updated = prev.map(p => p.id === productId ? { ...p, cases: num } : p);
      console.log('🔍 Frontend - Updated selectedProducts after cases change:', updated);
      return updated;
    });
  };

  const handleBottlesChange = (productId, value) => {
    const num = Math.max(0, parseInt(value) || 0);
    console.log('🔍 Frontend - handleBottlesChange:', { productId, value, num });
    setSelectedProducts(prev => {
      const updated = prev.map(p => p.id === productId ? { ...p, bottles: num } : p);
      console.log('🔍 Frontend - Updated selectedProducts after bottles change:', updated);
      return updated;
    });
  };

  const validateAndMark = useCallback((productId) => {
    if (shiftType !== 'out') return;
    const product = selectedProducts.find(p => p.id === productId);
    if (!product) return;
    const requested = (product.cases || 0) * (product.packQuantity || 0) + (product.bottles || 0);
    // Robust available lookup for the business date
    const getAvailable = () => {
      let avail = Number(product.available || 0);
      if (!avail) {
        const match = (shopProductsCache || []).find(sp => {
          const bn = sp.brand_number || sp.brandNumber;
          const bnP = product.brandNumber;
          const sz = Number(sp.size_ml || sp.size || 0);
          const szP = Number(product.sizeMl || 0);
          return bn && bnP && String(bn) === String(bnP) && sz === szP;
        });
        if (match) {
          avail = Number((match.closingStock ?? match.closing_stock) ?? (match.totalStock ?? match.total_stock) ?? (match.quantity ?? match.current_quantity) ?? 0);
        }
      }
      return avail;
    };
    const available = getAvailable();
    setRowErrors(prev => {
      const next = { ...prev };
      if (requested > available) {
        next[productId] = `${product.name}: available ${available}, requested ${requested}`;
      } else {
        delete next[productId];
      }
      return next;
    });
  }, [shiftType, selectedProducts, shopProductsCache]);

  const handleRemoveProduct = (productId) => {
    setSelectedProducts(prev => prev.filter(p => p.id !== productId));
  };

  const openRemoveConfirm = (product) => setRemoveTarget(product);
  const closeRemoveConfirm = () => setRemoveTarget(null);
  const confirmRemove = () => {
    if (removeTarget) {
      handleRemoveProduct(removeTarget.id);
      setRemoveTarget(null);
    }
  };

  const handleConfirmShift = async () => {
    const requestId = Date.now() + Math.random();
    console.log(`\n🚀 [${requestId}] ===== SHIFT TRANSFER OPERATION STARTED =====`);
    console.log(`🔍 [${requestId}] Frontend - handleConfirmShift called with state:`, {
      selectedStore,
      selectedProducts: selectedProducts.length,
      shiftType,
      loading,
      shopName: shopName,
      currentShop: user
    });
    console.log(`📋 [${requestId}] Selected Products Details:`, selectedProducts.map(p => ({
      id: p.id,
      name: p.name,
      brandNumber: p.brandNumber,
      cases: p.cases,
      bottles: p.bottles,
      totalQuantity: (p.cases * p.packQuantity) + p.bottles,
      available: p.available,
      mrp: p.mrp
    })));
    
    try {
      console.log(`\n🔍 [${requestId}] ===== VALIDATION PHASE =====`);
      
      // Validation
      if (!selectedStore) {
        console.log(`❌ [${requestId}] Validation failed: No store selected`);
        setError('Please select a store');
        return;
      }
      console.log(`✅ [${requestId}] Store validation passed:`, selectedStore);

      if (selectedProducts.length === 0) {
        console.log(`❌ [${requestId}] Validation failed: No products selected`);
        setError('Please add at least one product');
        return;
      }
      console.log(`✅ [${requestId}] Products validation passed: ${selectedProducts.length} products selected`);

      // Business rules temporarily disabled - allow all operations

      const productsWithQuantity = selectedProducts.filter(p => (p.cases > 0 || p.bottles > 0));
      if (productsWithQuantity.length === 0) {
        console.log(`❌ [${requestId}] Validation failed: No quantities entered`);
        setError('Please enter quantities for the products');
        return;
      }
      console.log(`✅ [${requestId}] Quantity validation passed: ${productsWithQuantity.length} products with quantities`);

      // Debug: Log the selectedProducts state
      console.log(`📊 [${requestId}] Frontend - selectedProducts state:`, selectedProducts);
      console.log(`📊 [${requestId}] Frontend - productsWithQuantity:`, productsWithQuantity);
      
      // Additional validation for shift out
      if (shiftType === 'out') {
        console.log(`🔍 [${requestId}] Shift Out validation - checking stock availability`);
        const stockErrors = Object.keys(rowErrors);
        if (stockErrors.length > 0) {
          console.log(`❌ [${requestId}] Stock validation failed:`, rowErrors);
          setError('Insufficient stock for some products');
          return;
        }
        console.log(`✅ [${requestId}] Stock validation passed: All products have sufficient stock`);
      }

      console.log(`\n🔄 [${requestId}] ===== PROCESSING PHASE =====`);
      setLoading(true);
      setError('');

      const currentShop = user;
      
      console.log(`🔑 [${requestId}] Authentication details:`, {
        currentShop: currentShop,
        shopName: shopName
      });

      console.log(`📦 [${requestId}] Processing ${productsWithQuantity.length} products for ${shiftType.toUpperCase()} operation`);

      // Process each product
      for (const product of productsWithQuantity) {
        const totalQuantity = (product.cases * product.packQuantity) + product.bottles;
        
        console.log(`\n🔍 [${requestId}] ===== PROCESSING PRODUCT ${productsWithQuantity.indexOf(product) + 1}/${productsWithQuantity.length} =====`);
        console.log(`📋 [${requestId}] Product Details:`, {
          id: product.id,
          name: product.name,
          brandNumber: product.brandNumber,
          shiftType: shiftType,
          totalQuantity: totalQuantity,
          cases: product.cases,
          bottles: product.bottles,
          packQuantity: product.packQuantity,
          available: product.available,
          mrp: product.mrp
        });
        
        // Determine if this is an internal shop transfer
        const isInternalTransfer = selectedStore?.startsWith('store_');
        const storeInfo = stores.find(s => s.id === selectedStore);
        
        console.log(`🔍 [${requestId}] Transfer Type Analysis:`, {
          selectedStore: selectedStore,
          isInternalTransfer: isInternalTransfer,
          storeInfo: storeInfo,
          isFromTGBCL: selectedStore === 'tgbcl'
        });
        
        const shiftData = {
          ...(shiftType === 'out' 
            ? { shopInventoryId: product.id } 
            : (isInternalTransfer 
              ? { shopInventoryId: product.id }  // Internal shop: product.id is shopInventoryId
              : { masterBrandId: product.id }    // External/TGBCL: product.id is masterBrandId
            )
          ),
          quantity: shiftType === 'out' ? -totalQuantity : totalQuantity,
          storeName: storeInfo?.shop_name || 'Unknown',
          isFromTGBCL: selectedStore === 'tgbcl',
          storeCode: selectedStore === 'tgbcl' ? 'TGBCL' : (storeInfo?.retailer_code || null),
          storeShopId: selectedStore?.startsWith('store_') ? parseInt(selectedStore.replace('store_','')) : null,
          shiftType: shiftType,
          tenTimesCompleted: selectedStore === 'tgbcl' ? tenTimesCompleted : false  // For TGBCL retail excise tax
        };
        
        console.log(`📤 [${requestId}] Frontend - Prepared shift data:`, {
          ...shiftData,
          quantitySign: shiftType === 'out' ? 'NEGATIVE (outgoing)' : 'POSITIVE (incoming)',
          productIdentifier: shiftType === 'out' ? 'shopInventoryId' : (isInternalTransfer ? 'shopInventoryId' : 'masterBrandId'),
          transferDirection: shiftType === 'out' ? 'FROM current shop TO destination' : 'FROM source TO current shop'
        });

        console.log(`🌐 [${requestId}] Frontend - Making API call to /api/stock-shift`);
        console.log(`📡 [${requestId}] API Request Details:`, {
          body: shiftData
        });
        
        const result = await apiPost('/api/stock-shift', shiftData);
        console.log(`✅ [${requestId}] Frontend - Shift successful for product:`, {
          productId: product.id,
          productName: product.name,
          result: result
        });
      }

      console.log(`\n🎉 [${requestId}] ===== SHIFT TRANSFER COMPLETED SUCCESSFULLY =====`);
      console.log(`✅ [${requestId}] All ${productsWithQuantity.length} products processed successfully`);
      setSuccess('Stock successfully shifted.');
      
      // Reset form
      console.log(`🔄 [${requestId}] Resetting form state`);
      setSelectedProducts([]);
      setSelectedStore('');
      setSearchTerm('');
      setTenTimesCompleted(false);
      
    } catch (error) {
      console.log(`\n❌ [${requestId}] ===== SHIFT TRANSFER FAILED =====`);
      console.error(`💥 [${requestId}] Error confirming shift:`, {
        error: error,
        message: error.message,
        stack: error.stack,
        requestId: requestId
      });
      setError(error.message || 'Failed to process shift');
    } finally {
      console.log(`🏁 [${requestId}] ===== SHIFT TRANSFER OPERATION ENDED =====`);
      setLoading(false);
    }
  };

  return (
    <>
      <main className="w-full px-4 py-8 space-y-8 max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="space-y-2 text-left">
          <h1 className="text-4xl font-bold text-left">Stock Transfer</h1>
          <p className="text-base text-muted-foreground text-left">Manage stock movements between stores and suppliers</p>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Success Alert */}
        {success && (
          <Alert className="border-green-600">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-900">{success}</AlertDescription>
          </Alert>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-left">Transfer Configuration</CardTitle>
            <CardDescription className="text-left">Select transfer direction and choose store</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Transfer Type Selection with better visual flow */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Shift In Option */}
              <button
                type="button"
                onClick={() => setShiftType('in')}
                className={`relative p-6 rounded-lg border-2 transition-all text-left ${
                  shiftType === 'in'
                    ? 'border-primary bg-primary/5 shadow-md'
                    : 'border-border hover:border-primary/50 hover:bg-accent'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-full ${shiftType === 'in' ? 'bg-primary' : 'bg-muted'}`}>
                    <ArrowDownCircle className={`h-6 w-6 ${shiftType === 'in' ? 'text-primary-foreground' : 'text-muted-foreground'}`} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg mb-1">Receive Stock</h3>
                    <p className="text-sm text-muted-foreground">
                      Transfer stock IN from suppliers or other stores
                    </p>
                  </div>
                </div>
              </button>

              {/* Shift Out Option */}
              <button
                type="button"
                onClick={() => setShiftType('out')}
                className={`relative p-6 rounded-lg border-2 transition-all text-left ${
                  shiftType === 'out'
                    ? 'border-primary bg-primary/5 shadow-md'
                    : 'border-border hover:border-primary/50 hover:bg-accent'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-full ${shiftType === 'out' ? 'bg-primary' : 'bg-muted'}`}>
                    <ArrowUpCircle className={`h-6 w-6 ${shiftType === 'out' ? 'text-primary-foreground' : 'text-muted-foreground'}`} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg mb-1">Send Stock</h3>
                    <p className="text-sm text-muted-foreground">
                      Transfer stock OUT to other stores
                    </p>
                  </div>
                </div>
              </button>
            </div>

            {/* Store Selection with clearer context */}
            <div className="space-y-3 pt-4 border-t">
              <div className="flex items-center gap-2">
                {shiftType === 'in' ? (
                  <ArrowDownToLine className="h-5 w-5 text-primary" />
                ) : (
                  <ArrowUpFromLine className="h-5 w-5 text-primary" />
                )}
                <Label className="text-left text-base font-semibold">
                  {shiftType === 'in' ? 'Receive From' : 'Send To'}
                </Label>
                <Badge variant={shiftType === 'in' ? 'default' : 'secondary'}>
                  {shiftType === 'in' ? 'Source' : 'Destination'}
                </Badge>
              </div>
              <Select value={selectedStore} onValueChange={setSelectedStore}>
                <SelectTrigger className="h-12">
                  <SelectValue placeholder="Choose a store..." />
                </SelectTrigger>
                <SelectContent>
                  {(() => {
                    // Business rules for store selection
                    if (shiftType === 'out') {
                      // Shift Out: Can transfer TO internal and external shops (exclude TGBCL)
                      return stores.filter(s => s.id !== 'tgbcl').map(store => (
                        <SelectItem key={store.id} value={store.id}>
                          {store.shop_name} {store.retailer_code !== store.shop_name ? `(${store.retailer_code})` : ''}
                        </SelectItem>
                      ));
                    } else {
                      // Shift In: Can only receive FROM external stores and TGBCL
                      // Internal stores are not shown for shift-in operations
                      return stores.map(store => (
                        <SelectItem key={store.id} value={store.id}>
                          {store.shop_name} {store.retailer_code !== store.shop_name ? `(${store.retailer_code})` : ''}
                        </SelectItem>
                      ));
                    }
                  })()}
                </SelectContent>
              </Select>
              {selectedStore && (
                <p className="text-sm text-muted-foreground">
                  {shiftType === 'in' 
                    ? `✓ Stock will be added to your inventory from this source`
                    : `✓ Stock will be sent from your inventory to this destination`
                  }
                </p>
              )}
              
              {/* TGBCL 10X Completed Checkbox */}
              {selectedStore === 'tgbcl' && shiftType === 'in' && (
                <div className="mt-4 p-4 border rounded-lg bg-blue-50">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="tenTimesCompleted"
                      checked={tenTimesCompleted}
                      onChange={(e) => setTenTimesCompleted(e.target.checked)}
                      className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
                    />
                    <div className="flex-1">
                      <Label 
                        htmlFor="tenTimesCompleted" 
                        className="text-sm font-semibold cursor-pointer"
                      >
                        10X Completed (Apply 10% Retail Excise Turnover Tax)
                      </Label>
                      <p className="text-xs text-muted-foreground mt-1">
                        Check this if you've completed 10 times turnover. This will add 10% retail excise turnover tax to the invoice calculation.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Product Search Card */}
        <Card ref={searchContainerRef}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-left">
              <Search className="h-6 w-6" />
              Product Search
            </CardTitle>
            <CardDescription className="text-left">Search and add products to transfer</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="relative">
              <Input
                ref={searchInputRef}
                type="text"
                placeholder="Search by brand number, brand name..."
                value={searchTerm}
                onChange={handleInputChange}
                onKeyDown={handleKeyPress}
                onFocus={() => searchTerm.trim().length >= MIN_SEARCH_LENGTH && setShowSearchResults(true)}
                className="pr-10"
              />
              <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            </div>
            
            {showSearchResults && searchResults.length > 0 && (
              <div className="mt-4 border rounded-lg overflow-hidden max-h-96 overflow-y-auto">
                <div className="divide-y">
                  {searchResults.map(brand => (
                    <div
                      key={brand.id}
                      className="p-4 hover:bg-accent cursor-pointer transition-colors"
                      onClick={() => handleBrandSelect(brand)}
                    >
                      <div className="text-left">
                        <p className="font-medium mb-1">{brand.brand_name || brand.name}</p>
                        <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                          <Badge variant="outline" className="font-mono">#{brand.brand_number || brand.brandNumber}</Badge>
                          <Badge variant="secondary">{(brand.sizeMl || brand.size_ml || brand.size)} ml</Badge>
                          {(brand.packType || brand.pack_type) && (
                            <Badge variant="secondary">{brand.packType || brand.pack_type}</Badge>
                          )}
                          <Badge variant="secondary">₹{brand.standard_mrp ?? brand.mrp}</Badge>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Selected Products Card */}
        {selectedProducts.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <div className="mx-auto h-16 w-16 rounded-full bg-muted flex items-center justify-center mb-4">
                <Search className="h-8 w-8 text-muted-foreground" />
              </div>
              <h4 className="text-lg font-medium mb-2">Add products to initiate transfer</h4>
              <p className="text-sm text-muted-foreground">Use the Product Search above to find and add brands</p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between text-left">
                <span className="text-left">Selected Products</span>
                <Badge variant="secondary">{selectedProducts.length} items</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[60px] text-center">S.No</TableHead>
                      <TableHead className="min-w-[250px] text-center">Brand Name</TableHead>
                      <TableHead className="min-w-[120px] text-center">Size</TableHead>
                      <TableHead className="min-w-[140px] text-center">Cases</TableHead>
                      <TableHead className="min-w-[140px] text-center">Bottles</TableHead>
                      <TableHead className="min-w-[100px] text-center">M.R.P</TableHead>
                      <TableHead className="w-[80px] text-center">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedProducts.map((product, index) => (
                      <React.Fragment key={product.id}>
                      <TableRow className={rowErrors[product.id] ? 'bg-red-50' : ''}>
                        <TableCell className="font-medium text-center align-top py-3">{index + 1}</TableCell>
                        <TableCell className="text-center align-top py-3">
                          <p className="font-medium">
                            {product.name} ({product.brandNumber}{product.packType ? `, ${product.packType}` : ''})
                          </p>
                        </TableCell>
                        <TableCell className="text-center align-top py-3">{product.packQuantity} × {product.sizeMl} ml</TableCell>
                        <TableCell className="align-top py-3">
                          <div className="flex flex-col items-center gap-1 justify-start">
                            <Input
                              type="number"
                              min="0"
                              className="w-20 text-center h-9 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                              placeholder="0"
                              value={product.cases === 0 ? '' : product.cases}
                              onWheel={(e) => e.currentTarget.blur()}
                              onFocus={(e) => e.target.select()}
                              onBlur={() => validateAndMark(product.id)}
                              onChange={(e) => handleCasesChange(product.id, e.target.value)}
                            />
                            <span className="text-xs h-[20px]">&nbsp;</span>
                          </div>
                        </TableCell>
                        <TableCell className="align-top py-3">
                          <div className="flex flex-col items-center gap-1 justify-start">
                            <Input
                              type="number"
                              min="0"
                              className="w-20 text-center h-9 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                              placeholder="0"
                              value={product.bottles === 0 ? '' : product.bottles}
                              onWheel={(e) => e.currentTarget.blur()}
                              onFocus={(e) => e.target.select()}
                              onBlur={() => validateAndMark(product.id)}
                              onChange={(e) => handleBottlesChange(product.id, e.target.value)}
                            />
                            <span className="text-xs text-green-600 font-medium">
                            Available: {(() => {
                              // Get available quantity for this product
                              let available = 0;
                              
                              if (shiftType === 'out') {
                                // For shift out, get from current shop's inventory
                                const match = (shopProductsCache || []).find(sp => {
                                  const bn = sp.brand_number || sp.brandNumber;
                                  const bnP = product.brandNumber;
                                  const sz = Number(sp.size_ml || sp.size || 0);
                                  const szP = Number(product.sizeMl || 0);
                                  return bn && bnP && String(bn) === String(bnP) && sz === szP;
                                });
                                available = Number((product.available || 0) || ((match && ((match.closingStock ?? match.closing_stock) ?? (match.totalStock ?? match.total_stock) ?? (match.quantity ?? match.current_quantity))) || 0));
                                console.log('🔍 Shift Out - Available calculation:', {
                                  productId: product.id,
                                  productAvailable: product.available,
                                  matchFound: !!match,
                                  matchAvailable: match ? (match.closingStock ?? match.closing_stock) ?? (match.totalStock ?? match.total_stock) ?? (match.quantity ?? match.current_quantity) : null,
                                  finalAvailable: available
                                });
                              } else {
                                // For shift in, get from selected supplier's inventory
                                available = Number(product.available || 0);
                                console.log('🔍 Shift In - Available calculation:', {
                                  productId: product.id,
                                  productAvailable: product.available,
                                  finalAvailable: available
                                });
                              }
                              
                              return isNaN(available) ? 0 : available;
                            })()}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-center align-top py-3">{formatINR(product.mrp)}</TableCell>
                        <TableCell className="text-center align-top py-3">
                          <div className="flex items-start justify-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-9"
                              onClick={() => openRemoveConfirm(product)}
                              title="Remove product"
                              aria-label={`Remove ${product.name}`}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                      {rowErrors[product.id] && (
                        <TableRow>
                          <TableCell></TableCell>
                          <TableCell colSpan={6}>
                            <Alert variant="destructive" className="py-2">
                              <AlertCircle className="h-4 w-4" />
                              <AlertDescription className="text-xs">
                                Insufficient stock. {rowErrors[product.id]}
                              </AlertDescription>
                            </Alert>
                          </TableCell>
                        </TableRow>
                      )}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                <Button
                  variant="outline"
                  className="flex-1 h-11"
                  onClick={() => {
                    setSelectedProducts([]);
                    setSearchTerm('');
                    setTenTimesCompleted(false);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 h-11"
                  onClick={handleConfirmShift}
                  disabled={
                    loading ||
                    !selectedStore ||
                    selectedProducts.length === 0 ||
                    !selectedProducts.some(p => (p.cases > 0 || p.bottles > 0)) ||
                    (shiftType === 'out' && Object.keys(rowErrors).length > 0)
                  }
                >
                  {loading ? 'Processing...' : 'Confirm Stock Transfer'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Remove Confirmation Modal */}
      {removeTarget && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" 
          onClick={closeRemoveConfirm}
        >
          <Card 
            className="w-full max-w-md mx-4" 
            onClick={(e) => e.stopPropagation()} 
            role="dialog" 
            aria-modal="true" 
            aria-labelledby="remove-title"
          >
            <CardHeader>
              <CardTitle id="remove-title" className="text-left">Remove product?</CardTitle>
              <CardDescription className="text-left">
                {removeTarget.name} ({removeTarget.brandNumber}{removeTarget.packType ? `, ${removeTarget.packType}` : ''})
              </CardDescription>
            </CardHeader>
            <CardContent className="flex gap-4">
              <Button variant="outline" className="flex-1" onClick={closeRemoveConfirm}>
                Cancel
              </Button>
              <Button variant="destructive" className="flex-1" onClick={confirmRemove}>
                Remove
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
}

export default ShiftTransfer;